import './MarkdownViewer.css'
import { type AnchorHTMLAttributes, type HTMLAttributes, type ImgHTMLAttributes, type ReactNode, useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { cn } from '@/utilities/misc'
import remarkGfm from 'remark-gfm'
import rehypeStringify from 'rehype-stringify'
import rehypeRaw from 'rehype-raw'
import remarkParse from 'remark-parse'
import remarkRehype from 'remark-rehype'
import { Icon } from '../graphics/Icon'
import { Link } from 'react-router-dom'
import { PUB_CDN } from '@/utilities/envVariables'
import { fillMarkdownPlugins } from '@/utilities/markdownPlugin'

type Props = {
  value: string | undefined,
  articleId?: bigint,
  withinEditor?: boolean,
  allowHtml?: boolean,
  allowPlugins?: boolean,
  localLinksTo?: string,
  linksToNewTabs?: boolean,  // With <MarkdownEditor> and other editors you don't want the user to leave the page
  headersStartLevel?: number,  // Maps <h1> to <h(n)>, <h2> to <h(n + 1)>, etc
}

export function MarkdownViewer(props: Props) {
  const allowPlugins = !!props.allowPlugins
  const [pluginFilled, setPluginFilled] = useState<string | null>(null)

  useEffect(() => {
    if (!allowPlugins) {
      setPluginFilled(null)
      return
    }
    let cancelled = false
    const text = props.value ?? '?'
    fillMarkdownPlugins(text).then((filled) => {
      if (!cancelled) setPluginFilled(filled)
    })
    return () => {
      cancelled = true
    }
  }, [props.value, allowPlugins])

  const headersStartLevel = props.headersStartLevel ?? 3
  // const [zoomImageSrc, setZoomImageSrc] = useState<string>()
  // const [zoomImageCaption, setZoomImageCaption] = useState<string>()
  // const [showExtsExplanation, setShowExtsExplanation] = useState(false)

  //----------------------------------------------------------------------
  function h1Transform(props2: HTMLAttributes<HTMLHeadingElement>) {
    const node = (props2 as any).node
    const level = +node.tagName.substring(1)
    const HeaderEl = headers[level + headersStartLevel - 2] ??
      headers[5]!  // Fall back to <h6>
    let newProps = { ...props2 }
    delete (newProps as any).node
    delete newProps.children

    // The contents of the <h1> (or whatever) should always be a string
    let titleText = props2.children as string
    let id: string | undefined

    if (typeof titleText === 'string') {
      // The header title may optionally end with '[#some_hash_link_name]' 
      // representing the ID to give to the <h1> element for hash linking
      let match = titleText?.match(/^(.*)\[#([^\]]+)\]\s*$/)
      if (match) {
        id = match[2]
        titleText = match[1]?.trim() ?? ''
      }
    }

    return (
      <HeaderEl
        {...newProps}
        id={id}
        className={'Header' + level}
      >
        {titleText}
      </HeaderEl>
    )
  }

  //----------------------------------------------------------------------
  function aHrefTransform(props2: AnchorHTMLAttributes<HTMLAnchorElement>) {
    let href = props2.href
    const children = replaceBackLinkPrefix(props2.children)

    // <iframe width="1386" height="789" src="https://www.youtube.com/embed/xgZtsJJ_8gA" title="What is SHD to you as a Zazzle designer" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>

    if (href?.startsWith('https://www.youtube.com/watch?v=')) {
      const videoId = href.substring('https://www.youtube.com/watch?v='.length)
      const url = 'https://www.youtube.com/embed/' + videoId
      return (
        <div className='YouTubeEmbed'>
          <iframe
            className='YouTubeEmbed'
            src={url}
            title={children as any}
            allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
            referrerPolicy='strict-origin-when-cross-origin'
            allowFullScreen
          />
          <label><a
            target='_blank'
            href={href}
          >
            {children}<Icon icon='externalLink' />
          </a></label>
        </div>
      )
    }

    if (href?.startsWith('#')) {
      return (
        <a href={ window.location.pathname + href}>
          {children}
        </a>
      )
    }

    // if (href?.toLowerCase().startsWith(process.env.NEXT_PUBLIC_BASE_URL!))
    //   href = href.substring(process.env.NEXT_PUBLIC_BASE_URL!.length)

    const isLocal = !!href?.match(/^\/([a-z0-9]|$)/i)
    const linksToNewTabs = !!props.linksToNewTabs

    if (isLocal && props.localLinksTo) {
      href = props.localLinksTo + encodeURIComponent(href!)
        .replace('%23', '#')
    }

    const target = (isLocal && !linksToNewTabs) ? '_self' : '_blank'

    const elProps: any = { ...props2 }
    delete elProps.node

    return (
      <Link
        {...elProps}
        target={target}
        to={href}
        // onClick={() => {
        //   if (!linksToNewTabs) {
        //     apiMiscTrack.call({
        //       event_type: eventType,
        //       item_type: props.itemType,
        //       item_id: props.itemId,
        //       url: href,
        //       referrer: '~' + router.asPath,
        //       details: 'MarkdownViewer: ' + props2.children?.toString(),
        //     })
        //   }
        // }}
      >
        {children}
        {!isLocal && (
          <Icon className='ExternalLink' icon='externalLink' />
        )}
      </Link>
    )
  }

  //----------------------------------------------------------------------
  function tablefTransform(props2: AnchorHTMLAttributes<HTMLTableElement>) {
    const elProps: any = { ...props2 };
    delete elProps.node;
    return (
      <table {...elProps} className={props2.className ?? 'ContentTable'}>
        {props2.children}
      </table>
    )
  }

  //----------------------------------------------------------------------
  function imgTransform(props2: ImgHTMLAttributes<HTMLImageElement>) {

    // URL may be pipe-delimited: url|WIDTHxHEIGHT|scale%
    // Remark percent-encodes those pipes, so they often arrive as %7C / %25.
    const [rawSrc = '', sizePart = '', scalePart = ''] = (props2.src ?? '').split(/\||%7C/i)
    let src = rawSrc

    if (src.startsWith('@/') && src.length >= 3) {  // Asset
      const queryString = src.match(/\?.*$/)?.[0] ?? ''
      if (queryString !== '')
        src = src.substring(0, src.length - queryString.length)
      const parts = src.substring(2).split('/')
      if (parts.length >= 1 && parts.length <= 2) {
        try {
          const targetArticleId = parts.length === 1
            ? props.articleId
            : BigInt(parts[0]!)
          const assetName = parts[parts.length - 1]!
          src = `${PUB_CDN}/article/${targetArticleId}/asset/${assetName}${queryString}`
        } catch { }
      }
    }

    let width: number | undefined
    let height: number | undefined
    const sizeMatch = safeDecode(sizePart).trim().match(/^(\d+)x(\d+)$/i)
    if (sizeMatch) {
      const scaleMatch = safeDecode(scalePart).trim().match(/^(\d+(?:\.\d+)?)%$/)
      const scale = scaleMatch ? Number(scaleMatch[1]) / 100 : 1
      width = Math.round(Number(sizeMatch[1]) * scale)
      height = Math.round(Number(sizeMatch[2]) * scale)
    }

    const elProps: any = { ...props2 };
    delete elProps.node;

    return (<>
      <img
        {...elProps}
        alt={props2.alt}
        className={cn('Image', props2.className)}
        src={src}
        width={width}
        height={height}
        // onClick={() => {
        //   setZoomImageSrc(src);
        //   setZoomImageCaption(props2.alt);
        // }}
      />
    </>)
  }

  const remarkPlugins: any /*PluggableList*/ = props.allowHtml
    ? [remarkGfm, remarkParse, remarkRehype, rehypeRaw, /*rehypeSanitize,*/ rehypeStringify]
    : [remarkGfm];
  
  const remarkOptions: any /*RemarkRehypeOptions*/ = {
    allowDangerousHtml: !!props.allowHtml,
  }

  if (allowPlugins && pluginFilled === null) {
    return (
      <div className='MarkdownViewer'>
        <div className='PluginsLoading'>Loading…</div>
      </div>
    )
  }

  let markdownText = allowPlugins && pluginFilled !== null
    ? pluginFilled
    : (props.value ?? '?')

  //--------------------------------------------------------------------------------
  // Replace [TOC] with a table of contents
  markdownText = markdownText.replace(/(^|\n)\s*\[TOC\]\s*\n/gs, (match: string) => {
    const headerLines = markdownText
      .split('\n')
      .filter(line => line.trimStart().startsWith('#'))

    const tocLines: string[] = []
    for (const line of headerLines) {
      const match = line.match(/^(#{1,6})\s+(.*)\s*\[#([^\]]+)\]\s*$/)
      if (!match) continue
      const level = match[1]!.length
      const title = match[2]!.trim()
      const id = match[3]!.trim()
      tocLines.push(`${'  '.repeat(level - 1)}- [${title}](#${id})`)
    }
    return '\n\n' + tocLines.join('\n') + '\n\n'
  })

  //--------------------------------------------------------------------------------
  // Render
  return (<>

    <div className='MarkdownViewer'>
      <ReactMarkdown
        //className='MarkdownViewer'
        components={{
          h1: h1Transform,
          h2: h1Transform,
          h3: h1Transform,
          h4: h1Transform,
          h5: h1Transform,
          h6: h1Transform,
          a: aHrefTransform,
          img: imgTransform,
          table: tablefTransform,
        }}
        remarkPlugins={remarkPlugins}
        remarkRehypeOptions={remarkOptions}
      >
        {markdownText}
      </ReactMarkdown>
    </div>

    {/* <ArticlePopup
      code='no-external-links'
      show={showExtsExplanation}
      setShow={setShowExtsExplanation}
    />

    <LightBox
      images={zoomImageSrc
        ? [{ url: zoomImageSrc, title: zoomImageCaption }]
        : []
      }
      show={!!zoomImageSrc}
      onHide={() => setZoomImageSrc(undefined)}
    /> */}

  </>);
}

//--------------------------------------------------------------------------------
const headers = [
  (props: any) => <h1 {...props} />,
  (props: any) => <h2 {...props} />,
  (props: any) => <h3 {...props} />,
  (props: any) => <h4 {...props} />,
  (props: any) => <h5 {...props} />,
  (props: any) => <h6 {...props} />,
]

//--------------------------------------------------------------------------------
function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

//--------------------------------------------------------------------------------
/** Replace a leading "< " in link text with a back-pointer glyph. */
function replaceBackLinkPrefix(children: ReactNode): ReactNode {
  if (typeof children === 'string' && children.startsWith('< ')) {
    return '◀ ' + children.slice(2)
  }
  if (Array.isArray(children) && typeof children[0] === 'string' && children[0].startsWith('< ')) {
    return ['◀ ' + children[0].slice(2), ...children.slice(1)]
  }
  return children
}
